import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import useTranslation from "@babackapp/translations/use-translation";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import NumberFlow from "@number-flow/react";
import TextAreaWithSuggestions from "@/pages/(app)/returns/refund/validate/text-area-with-suggestion";
import { ArticleItem } from "../article-item/article-item";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import { useLineItemRejectReasons } from "@/pages/(app)/returns/refund/hooks/use-line-item-reject-reason";
import { useMemo } from "react";
import { useRejectLineItemsListSections } from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-list-section";
import useRejectLineItemsQuantitiesSummary from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-quantities-summary";
import { useRejectLineItem } from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-context";
import {
  useNotReturned,
  useSetNotReturned,
} from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-state-context";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context";
import useGetItemsInActiveReturns, {
  type GetItemsInActiveReturnsResponse
} from "@babackapp/private-api-client/client/use-get-items-in-active-returns";
import { useConflictingItems } from "@/pages/(app)/returns/hooks/use-conflicting-items";
import { ConflictWarning } from "@/pages/(app)/returns/components/conflict-warning";

export default function ArticlesNotReturned() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const returnRequest = useCurrentReturnRequest();
  const { itemsNotReturned } = useRejectLineItemsListSections();
  const hasPermissionToView = useViewerHasAtLeastAdminRights();

  const { data: itemsInActiveReturns } = useGetItemsInActiveReturns({
    shopId: shop.id,
    returnRequestId: returnRequest.id,
    orderId: returnRequest.orderId
  });

  const summary = useRejectLineItemsQuantitiesSummary({
    state: "not-returned",
  });

  if (summary.max <= 0 || !hasPermissionToView) {
    return null;
  }

  return (
    <AccordionItem value="notReturned">
      <AccordionTrigger>
        <span>
          {t("returnDetail.confirmModal.notReturned")}
          <Badge variant="secondary" className="ml-2">
            <NumberFlow value={summary.selected} suffix={` / ${summary.max}`} />
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="border-border border rounded-md flex-col gap-4 divide-y overflow-hidden">
          {itemsNotReturned.map((lineItem) => (
            <ArticleNotTreatedBylogistician
              key={lineItem.line_item_id}
              lineItem={lineItem}
              itemsInActiveReturns={itemsInActiveReturns}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

const ArticleNotTreatedBylogistician = ({
  lineItem,
  itemsInActiveReturns,
}: {
  lineItem: ReturnRequestLineItemDto;
  itemsInActiveReturns: GetItemsInActiveReturnsResponse | undefined;
}) => {
  const { data: shop } = useCurrentShopSuspense();
  const { item, updateResolution, updateVariant } = useRejectLineItem(lineItem);
  const quantityValue = useNotReturned(lineItem.line_item_id);
  const updateQuantity = useSetNotReturned(lineItem.line_item_id);

  const {
    conflictingItems,
    adjustedMaxQuantity,
    isDisabled,
    warningMessage,
  } = useConflictingItems({
    lineItemId: lineItem.line_item_id,
    itemsInActiveReturns: itemsInActiveReturns?.itemsInOtherReturns,
    currentQuantity: quantityValue.selectedQuantity,
    maxQuantity: quantityValue.maxQuantity,
  });

  const hasConflict = conflictingItems.length > 0;

  // Get unique returns for the component
  const uniqueReturns = Array.from(
    new Map(conflictingItems.map(item => [item.returnRequestId, item])).values()
  );

  const displayMessage = hasConflict && warningMessage ? (
    <ConflictWarning
      warningMessage={warningMessage}
      conflictingReturns={uniqueReturns}
      adjustedMaxQuantity={adjustedMaxQuantity}
      shopId={shop.id}
    />
  ) : undefined;

  /**
  When an article is not returned, it has no resolution.
  When the merchant adds an article to the request, we need to add a resolution to it.
  So if the quantity is 0, remove the resolution, otherwise add it, default to refund
  */
  const _updateQuantity = (quantity: number) => {
    if (quantity === 0) updateResolution(null);
    else if (!item.resolution) updateResolution("refund");
    updateQuantity(quantity);
  };

  const { addLineItemReason, lineItemsReason } = useLineItemRejectReasons();
  const editineItemReason = addLineItemReason(lineItem.line_item_id);

  const selectLineItemReason = useMemo(() => {
    return lineItemsReason[lineItem.line_item_id]?.reason;
  }, [lineItemsReason, lineItem.line_item_id]);

  return (
    <>
      <ArticleItem
        lineItem={item}
        quantity={quantityValue.selectedQuantity}
        maxQuantity={adjustedMaxQuantity}
        onChangeQuantity={_updateQuantity}
        onChangeVariant={updateVariant}
        onChangeResolution={updateResolution}
        hideStatus
        hideResolution
        disabled={isDisabled}
        disabledReason={displayMessage}
      />
      {quantityValue.selectedQuantity > 0 && (
        <TextAreaWithSuggestions
          onChangeReason={editineItemReason}
          reason={selectLineItemReason}
        />
      )}
    </>
  );
};
